import { notify } from '@affine/component';
import type { WorkspaceServerService } from '@affine/core/modules/cloud/services/workspace-server';
import {
  type DocRole,
  grantDocGroupRoleMutation,
  grantDocMateRoleMutation,
  grantDocUserRolesMutation,
  permanentlyDeleteDocMutation,
  restoreDocMutation,
  revokeDocGroupRoleMutation,
  revokeDocMateRoleMutation,
  revokeDocUserRolesMutation,
  trashDocMutation,
  updateDocUserRoleMutation,
} from '@affine/graphql';
import { I18n } from '@affine/i18n';
import { OnEvent, Service } from '@toeverything/infra';

import type { ContainerCopyBeforeDeleteService } from '../../../components/delete-with-copy/container-copy-before-delete.service';
import {
  classifyOfflineMutationError,
  OFFLINE_DELETE_COPY_CONFIRMATION,
  OFFLINE_DELETE_TABLE_CHOICE,
  PERMANENT_DELETE_DOMAIN_CODES,
} from '../../offline-mutation-outbox/services/offline-mutation-error';
import {
  OfflineMutationDomainError,
  type OfflineMutationOutboxService,
} from '../../offline-mutation-outbox/services/offline-mutation-outbox';
import {
  OFFLINE_DELETION_HOLD_MS,
  type OfflineMutationRecord,
} from '../../offline-mutation-outbox/types';
import { hasAppBaseErrorCode } from '../../streamlain/hooks/app-base-error-code';
import type { Workspace } from '../../workspace';
import { WorkspaceInitialized } from '../../workspace/events';
import type { WorkspaceService } from '../../workspace/services/workspace';
import type { AccessibleDocCacheStore } from '../stores/accessible-doc-cache';
import { prepareDatabaseSystemDocsForServerMutation } from './database-system-doc-access';

export type DocGrantUserRolesPayload = {
  docId: string;
  userIds: string[];
  role: string;
};

export type DocGrantGroupRolesPayload = {
  docId: string;
  groupRoles: Array<{
    groupId: string;
    role: string;
  }>;
};

export type DocUpdateUserRolePayload = {
  docId: string;
  userId: string;
  role: string;
};

export type DocRevokeUserRolePayload = {
  docId: string;
  userId: string;
};

export type DocRevokeGroupRolePayload = {
  docId: string;
  groupId: string;
};

export type DocGrantMateRolePayload = {
  docId: string;
  mateId: string;
  role: string;
};

export type DocRevokeMateRolePayload = {
  docId: string;
  mateId: string;
};

@OnEvent(
  WorkspaceInitialized,
  (service: DocMutationService) => service.onWorkspaceInitialized
)
export class DocMutationService extends Service {
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly accessibleDocCache: AccessibleDocCacheStore,
    private readonly outbox: OfflineMutationOutboxService,
    private readonly copyBeforeDelete: ContainerCopyBeforeDeleteService
  ) {
    super();
    this.disposables.push(
      outbox.registerExecutor('doc.permanentDelete', record =>
        this.sendPermanentDelete(record)
      ),
      outbox.registerExecutor('doc.trash', record =>
        this.sendTrashState(record)
      ),
      outbox.registerExecutor('doc.restore', record =>
        this.sendTrashState(record)
      )
    );
    const subscription = outbox.failed$.subscribe(record => {
      if (
        (record.operation !== 'doc.trash' &&
          record.operation !== 'doc.restore' &&
          record.operation !== 'doc.permanentDelete') ||
        record.lastError === OFFLINE_DELETE_COPY_CONFIRMATION ||
        record.lastError === OFFLINE_DELETE_TABLE_CHOICE ||
        record.failure !== 'refused'
      )
        return;
      notify.error({
        title: I18n.t('com.streamlain.trash.doc-move-failed', {
          reason: record.lastError ?? '',
        }),
      });
      outbox.removeFailed(record.id).catch(console.error);
    });
    this.disposables.push(() => subscription.unsubscribe());
  }

  onWorkspaceInitialized(_workspace: Workspace): void {
    // Construct the executors when the workspace opens, including after reload.
  }

  async requestTrashState(
    docId: string,
    trashed: boolean
  ): Promise<{ mutationId: string }> {
    const operation = trashed ? 'doc.trash' : 'doc.restore';
    const existing = (await this.outbox.refreshPendingMutations()).find(
      record =>
        record.operation === operation &&
        (record.payload as { docId?: string }).docId === docId
    );
    const record = await this.outbox.enqueue({
      operation,
      payload: { docId },
      idempotencyKey: `${operation}:${docId}`,
      dedupePending: true,
      holdMs: trashed ? OFFLINE_DELETION_HOLD_MS : undefined,
      orderKey: `doc:${docId}`,
    });
    if (trashed && !existing) {
      notify(
        {
          title: I18n.t(
            'com.streamlain.dataview.database.deletionPending.queued'
          ),
          actions: [
            {
              key: 'cancel',
              label: I18n.t(
                'com.streamlain.dataview.database.deletionPending.cancel'
              ),
              autoClose: true,
              onClick: async () => {
                if (!(await this.outbox.cancelPending(record.id)))
                  await this.requestTrashState(docId, false);
              },
            },
          ],
        },
        { duration: OFFLINE_DELETION_HOLD_MS }
      );
    }
    return { mutationId: record.id };
  }

  private async sendTrashState(
    record: OfflineMutationRecord<'doc.trash' | 'doc.restore'>
  ): Promise<void> {
    const { docId } = record.payload;
    const trashed = record.operation === 'doc.trash';
    try {
      await this.ensureDocServerReady(docId);
      await this.requireServer().gql({
        query: trashed ? trashDocMutation : restoreDocMutation,
        variables: { workspaceId: record.workspaceId, docId },
      });
    } catch (error) {
      throw classifyOfflineMutationError(error, [
        'FORBIDDEN',
        'BAD_REQUEST',
        'NOT_FOUND',
      ]);
    }
    this.workspaceService.workspace.docCollection.meta.setDocMeta(docId, {
      trash: trashed,
      trashDate: trashed ? Date.now() : undefined,
    });
    await this.accessibleDocCache.fetchAccessibleDocs();
  }

  async requestPermanentDelete(
    docId: string,
    copyConfirmed = false
  ): Promise<{ mutationId: string }> {
    const record = await this.outbox.enqueue({
      operation: 'doc.permanentDelete',
      payload: { docId, copyConfirmed },
      idempotencyKey: `doc.permanentDelete:${docId}`,
      dedupePending: true,
      holdMs: OFFLINE_DELETION_HOLD_MS,
      orderKey: `doc:${docId}`,
    });
    return { mutationId: record.id };
  }

  private async sendPermanentDelete(
    record: OfflineMutationRecord<'doc.permanentDelete'>
  ): Promise<void> {
    const { docId } = record.payload;
    try {
      await this.copyBeforeDelete.checkBeforeSend(record);
      await this.ensureDocServerReady(docId);
      await this.requireServer().gql({
        query: permanentlyDeleteDocMutation,
        variables: { workspaceId: record.workspaceId, docId },
      });
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === OFFLINE_DELETE_COPY_CONFIRMATION
      )
        throw error;
      // The root page of an App Container goes with the container, which
      // asks first what happens to its tables.
      if (hasAppBaseErrorCode(error, OFFLINE_DELETE_TABLE_CHOICE))
        throw new OfflineMutationDomainError(OFFLINE_DELETE_TABLE_CHOICE);
      throw classifyOfflineMutationError(error, PERMANENT_DELETE_DOMAIN_CODES);
    }
    await this.accessibleDocCache.handleServerPermanentlyDeletedDoc(docId);
  }

  async grantUserRoles(payload: DocGrantUserRolesPayload): Promise<void> {
    await this.ensureDocServerReady(payload.docId);
    await this.requireServer().gql({
      query: grantDocUserRolesMutation,
      variables: {
        input: {
          workspaceId: this.workspaceService.workspace.id,
          docId: payload.docId,
          userIds: payload.userIds,
          role: payload.role as DocRole,
        },
      },
    });
    await this.accessibleDocCache.fetchAccessibleDocs();
  }

  async grantGroupRole(
    payload: DocGrantGroupRolesPayload['groupRoles'][number] & {
      docId: string;
    }
  ): Promise<void> {
    await this.grantGroupRoles({
      docId: payload.docId,
      groupRoles: [{ groupId: payload.groupId, role: payload.role }],
    });
  }

  async grantGroupRoles(payload: DocGrantGroupRolesPayload): Promise<void> {
    await this.ensureDocServerReady(payload.docId);
    for (const groupRole of payload.groupRoles) {
      await this.requireServer().gql({
        query: grantDocGroupRoleMutation,
        variables: {
          workspaceId: this.workspaceService.workspace.id,
          docId: payload.docId,
          groupId: groupRole.groupId,
          role: groupRole.role as DocRole,
        },
      });
    }
    await this.accessibleDocCache.fetchAccessibleDocs();
  }

  async updateUserRole(payload: DocUpdateUserRolePayload): Promise<void> {
    await this.ensureDocServerReady(payload.docId);
    await this.requireServer().gql({
      query: updateDocUserRoleMutation,
      variables: {
        input: {
          workspaceId: this.workspaceService.workspace.id,
          docId: payload.docId,
          userId: payload.userId,
          role: payload.role as DocRole,
        },
      },
    });
    await this.accessibleDocCache.fetchAccessibleDocs();
  }

  async revokeUserRole(payload: DocRevokeUserRolePayload): Promise<void> {
    await this.ensureDocServerReady(payload.docId);
    await this.requireServer().gql({
      query: revokeDocUserRolesMutation,
      variables: {
        input: {
          workspaceId: this.workspaceService.workspace.id,
          docId: payload.docId,
          userId: payload.userId,
        },
      },
    });
    await this.accessibleDocCache.fetchAccessibleDocs();
  }

  async revokeGroupRole(payload: DocRevokeGroupRolePayload): Promise<void> {
    await this.ensureDocServerReady(payload.docId);
    await this.requireServer().gql({
      query: revokeDocGroupRoleMutation,
      variables: {
        workspaceId: this.workspaceService.workspace.id,
        docId: payload.docId,
        groupId: payload.groupId,
      },
    });
    await this.accessibleDocCache.fetchAccessibleDocs();
  }

  async grantMateRole(payload: DocGrantMateRolePayload): Promise<void> {
    await this.ensureDocServerReady(payload.docId);
    await this.requireServer().gql({
      query: grantDocMateRoleMutation,
      variables: {
        workspaceId: this.workspaceService.workspace.id,
        docId: payload.docId,
        mateId: payload.mateId,
        role: payload.role as DocRole,
      },
    });
    await this.accessibleDocCache.fetchAccessibleDocs();
  }

  async revokeMateRole(payload: DocRevokeMateRolePayload): Promise<void> {
    await this.ensureDocServerReady(payload.docId);
    await this.requireServer().gql({
      query: revokeDocMateRoleMutation,
      variables: {
        workspaceId: this.workspaceService.workspace.id,
        docId: payload.docId,
        mateId: payload.mateId,
      },
    });
    await this.accessibleDocCache.fetchAccessibleDocs();
  }

  private requireServer() {
    const server = this.workspaceServerService.server;
    if (!server) {
      throw new Error('No server');
    }
    return server;
  }

  private async ensureDocServerReady(docId: string): Promise<void> {
    if (!this.accessibleDocCache.isLocallyCreated(docId)) {
      return;
    }

    await prepareDatabaseSystemDocsForServerMutation({
      collection: this.workspaceService.workspace.docCollection,
      docEngine: this.workspaceService.workspace.engine.doc,
      docId,
      workspaceId: this.workspaceService.workspace.id,
    });

    const confirmed = await this.accessibleDocCache.waitForServerConfirmed(
      docId,
      {
        timeoutMs: 10000,
        pollMs: 250,
      }
    );
    if (!confirmed) {
      throw new Error('Doc server confirmation pending');
    }
  }
}
